import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { USMap, StateInfo } from '@/components/Map';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { useBusinesses, type Business } from '@/pages/Admin/businesses/api/useBusinesses';

// ============================================================================
// Types
// ============================================================================

interface LocationData extends StateInfo {
  status: string;
  description: string;
  launchTimeframe?: string;
  schools?: string[];
  cities?: string[];
}

interface StatusOption {
  value: string;
  label: string;
  color: string;
}

type BusinessStatusFilter = 'all' | 'verified' | 'unverified';

// ============================================================================
// Constants
// ============================================================================

const API_URL = process.env.REACT_APP_API_URL;
const STORAGE_KEY = 'socialspark-map-locations';

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'active', label: 'Active', color: '#10b981' },
  { value: 'planned', label: 'Planned', color: '#6366f1' },
  { value: 'pilot', label: 'Pilot', color: '#f59e0b' },
];

const STATUS_ORDER = ['active', 'planned', 'pilot'];

const STATUS_STYLES: Record<string, { badge: string; border: string }> = {
  active: {
    badge: 'bg-green-500/20 text-green-400',
    border: 'bg-gradient-to-r from-green-500 to-green-400',
  },
  planned: {
    badge: 'bg-violet-400/20 text-violet-400',
    border: 'bg-gradient-to-r from-violet-400 to-violet-300',
  },
  pilot: {
    badge: 'bg-amber-500/20 text-amber-300',
    border: 'bg-gradient-to-r from-amber-500 to-amber-300',
  },
  default: {
    badge: 'bg-gray-500/20 text-gray-400',
    border: 'bg-gray-500',
  },
};

const BUSINESS_FILTER_OPTIONS: { value: BusinessStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
];

// ============================================================================
// Utility Functions
// ============================================================================

const getStatusBadgeClasses = (status: string): string => {
  const base = 'px-3 py-1.5 rounded-full text-xs font-semibold capitalize';
  const styles = STATUS_STYLES[status] || STATUS_STYLES.default;
  return `${base} ${styles.badge}`;
};

const getCardTopBorder = (status: string): string => {
  const styles = STATUS_STYLES[status] || STATUS_STYLES.default;
  return styles.border;
};

const sortLocationsByStatus = (
  entries: [string, LocationData][]
): [string, LocationData][] => {
  return entries.sort(
    (a, b) => STATUS_ORDER.indexOf(a[1].status) - STATUS_ORDER.indexOf(b[1].status)
  );
};

function filterBusinesses(businesses: Business[], filter: BusinessStatusFilter): Business[] {
  switch (filter) {
    case 'verified':
      return businesses.filter(b => b.verified);
    case 'unverified':
      return businesses.filter(b => !b.verified);
    default:
      return businesses;
  }
}

// ============================================================================
// Sub-Components
// ============================================================================

const HeroSection: React.FC<{ stateCount: number; businessCount: number }> = ({ stateCount, businessCount }) => (
  <section className="text-center pt-16 px-8 pb-12 relative overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[150%] h-full bg-[radial-gradient(ellipse_at_center_top,rgba(139,92,246,0.15)_0%,transparent_60%)] pointer-events-none" />

    <div className="max-w-[700px] mx-auto mb-10 relative z-10">
      <h1 className="text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-white via-white/80 to-violet-300 bg-clip-text text-transparent mb-4 tracking-tight">
        Social Spark{' '}
        <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
          Nationwide
        </span>
      </h1>
      <p className="text-lg text-white/60 leading-relaxed">
        Explore our reach across the United States. Hover over highlighted
        states to learn more about each location.
      </p>
    </div>

    <div className="flex justify-center gap-6 flex-wrap relative z-10">
      <StatCard value={stateCount} label="States Covered" />
      <StatCard value={businessCount} label="Businesses" />
    </div>
  </section>
);

const StatCard: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="bg-gradient-to-br from-[#0f0f1a]/80 to-[#1e1e32]/80 backdrop-blur-xl px-10 py-6 rounded-2xl border border-violet-500/20 min-w-[160px] shadow-[0_10px_40px_rgba(0,0,0,0.3),0_0_30px_rgba(139,92,246,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-[0_20px_60px_rgba(0,0,0,0.4),0_0_40px_rgba(139,92,246,0.15)]">
    <div className="text-5xl font-extrabold bg-gradient-to-r from-violet-500 to-violet-300 bg-clip-text text-transparent leading-none">
      {value}
    </div>
    <div className="text-sm text-white/50 mt-2 uppercase tracking-wider font-medium">
      {label}
    </div>
  </div>
);

interface SyncStatusBarProps {
  lastSynced: Date | null;
  syncError: string | null;
  isLoading: boolean;
  onSync: () => void;
}

const SyncStatusBar: React.FC<SyncStatusBarProps> = ({
  lastSynced,
  syncError,
  isLoading,
  onSync,
}) => (
  <div className="flex items-center justify-center gap-4 px-6 py-3 bg-slate-800/80 rounded-lg mx-auto max-w-[600px] my-4 flex-wrap">
    {lastSynced && (
      <span className="text-slate-400 text-sm">
        Last synced: {lastSynced.toLocaleTimeString()}
      </span>
    )}
    {syncError && (
      <span className="text-red-400 text-sm">Warning: {syncError}</span>
    )}
    <button
      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-none px-4 py-2 rounded-md cursor-pointer text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(59,130,246,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
      onClick={onSync}
      disabled={isLoading}
    >
      {isLoading ? 'Syncing...' : 'Sync'}
    </button>
  </div>
);

const LoadingSpinner: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-[600px] bg-slate-900/90 rounded-xl">
    <div className="w-10 h-10 border-[3px] border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
    <span className="mt-4 text-slate-400">Loading locations...</span>
  </div>
);

interface MapControlsBarProps {
  showBusinessPins: boolean;
  onToggleBusinessPins: () => void;
  showStateOverlay: boolean;
  onToggleStateOverlay: () => void;
  statusFilter: BusinessStatusFilter;
  onStatusFilterChange: (f: BusinessStatusFilter) => void;
  businessesLoading: boolean;
}

const MapControlsBar: React.FC<MapControlsBarProps> = ({
  showBusinessPins,
  onToggleBusinessPins,
  showStateOverlay,
  onToggleStateOverlay,
  statusFilter,
  onStatusFilterChange,
  businessesLoading,
}) => (
  <div className="flex flex-wrap items-center justify-center gap-3 px-6 py-3 bg-slate-800/60 rounded-lg mx-auto max-w-[900px] mb-6 border border-gray-700/40">
    {/* Layer toggles */}
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold mr-1">Layers</span>
      <button
        onClick={onToggleStateOverlay}
        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 border ${
          showStateOverlay
            ? 'bg-violet-500/20 text-violet-300 border-violet-500/40'
            : 'bg-slate-700/40 text-slate-500 border-slate-600/30 hover:text-slate-300'
        }`}
      >
        State Overlay
      </button>
      <button
        onClick={onToggleBusinessPins}
        disabled={businessesLoading}
        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 border ${
          showBusinessPins
            ? 'bg-violet-500/20 text-violet-300 border-violet-500/40'
            : 'bg-slate-700/40 text-slate-500 border-slate-600/30 hover:text-slate-300'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {businessesLoading ? 'Loading...' : 'Business Pins'}
      </button>
    </div>

    {/* Separator */}
    <div className="w-px h-6 bg-slate-600/50 mx-1" />

    {/* Status filter */}
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold mr-1">Filter</span>
      {BUSINESS_FILTER_OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => onStatusFilterChange(opt.value)}
          disabled={!showBusinessPins}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 border ${
            statusFilter === opt.value && showBusinessPins
              ? 'bg-violet-500/20 text-violet-300 border-violet-500/40'
              : 'bg-slate-700/40 text-slate-500 border-slate-600/30 hover:text-slate-300'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const MapLegend: React.FC = () => (
  <div className="flex flex-wrap justify-center gap-6 mt-6 p-5 bg-slate-800/50 rounded-xl border border-gray-700/50">
    {STATUS_OPTIONS.map((opt) => (
      <LegendItem key={opt.value} label={opt.label} color={opt.color} />
    ))}
  </div>
);

const LegendItem: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div className="flex items-center gap-2 text-sm text-slate-400">
    <div
      className="w-[18px] h-[18px] rounded border border-white/10"
      style={{ background: color }}
    />
    <span>{label}</span>
  </div>
);

interface LocationCardProps {
  abbr: string;
  location: LocationData;
}

const LocationCard: React.FC<LocationCardProps> = ({ abbr, location }) => (
  <div className="relative bg-gradient-to-br from-[#141423]/80 to-[#232337]/80 backdrop-blur-md rounded-2xl p-6 border border-violet-500/15 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-violet-500/30 hover:shadow-[0_20px_50px_rgba(0,0,0,0.3),0_0_30px_rgba(139,92,246,0.1)]">
    <div className={`absolute top-0 left-0 right-0 h-1 ${getCardTopBorder(location.status)}`} />
    <div className="flex justify-between items-center mb-3">
      <span className="text-2xl font-bold text-white">{abbr}</span>
      <span className={getStatusBadgeClasses(location.status)}>{location.status}</span>
    </div>
    <h3 className="text-xl text-white mb-2">
      {location.cities?.length ? location.cities.join(', ') : abbr}
    </h3>
    <p className="text-sm text-slate-400 leading-relaxed">{location.description}</p>
  </div>
);

// ============================================================================
// Custom Hook
// ============================================================================

interface UseLocationsResult {
  locations: Record<string, LocationData>;
  isLoading: boolean;
  lastSynced: Date | null;
  syncError: string | null;
  syncLocations: () => Promise<void>;
}

const useLocations = (): UseLocationsResult => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [locations, setLocations] = useState<Record<string, LocationData>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Use ref to track fetch status (persists across renders, doesn't cause re-renders)
  const fetchedRef = useRef(false);
  // Use ref to always have fresh accounts reference
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const fetchLocationsFromAPI = useCallback(async (): Promise<Record<string, LocationData> | null> => {
    const currentAccounts = accountsRef.current;

    if (!isAuthenticated || currentAccounts.length === 0) {
      return null;
    }

    try {
      const tokenResponse = { accessToken: await acquireApiToken(instance, currentAccounts[0]) };

      const token = tokenResponse.accessToken;

      const response = await fetch(`${API_URL}/locations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || `HTTP ${response.status}`);
      }

      if (data.success && data.locations) {
        setLastSynced(new Date());
        setSyncError(null);
        return data.locations;
      }

      throw new Error(data.error || 'Failed to fetch locations');
    } catch (error: any) {
      console.error('Failed to fetch locations:', error.message);
      setSyncError(error.message || 'Failed to fetch');
      return null;
    }
  }, [isAuthenticated, instance]);

  const syncLocations = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setSyncError(null);
    const apiLocations = await fetchLocationsFromAPI();

    if (apiLocations && Object.keys(apiLocations).length > 0) {
      setLocations(apiLocations);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiLocations));
    }

    setIsLoading(false);
  }, [fetchLocationsFromAPI]);

  // Load locations on mount - wait for authentication to be ready
  useEffect(() => {
    // Don't fetch if already done
    if (fetchedRef.current) {
      setIsLoading(false);
      return;
    }

    // Wait for MSAL to initialize - accounts might not be ready yet
    if (!isAuthenticated) {
      // Load from localStorage while waiting
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          setLocations(JSON.parse(saved));
        } catch {
          setLocations({});
        }
      }
      setIsLoading(false);
      return;
    }

    // Check if accounts are available
    const currentAccounts = accountsRef.current;
    if (currentAccounts.length === 0) {
      return; // Effect will re-run when accounts change
    }

    let isMounted = true;

    const loadLocations = async () => {
      setIsLoading(true);
      setSyncError(null);

      const apiLocations = await fetchLocationsFromAPI();

      if (!isMounted) return;

      if (apiLocations && Object.keys(apiLocations).length > 0) {
        setLocations(apiLocations);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(apiLocations));
        fetchedRef.current = true;
      } else {
        // Fallback to localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            setLocations(JSON.parse(saved));
          } catch {
            setLocations({});
          }
        }
      }

      setIsLoading(false);
    };

    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, accounts.length, fetchLocationsFromAPI]);

  // Persist to localStorage on change
  useEffect(() => {
    if (Object.keys(locations).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(locations));
    }
  }, [locations]);

  return { locations, isLoading, lastSynced, syncError, syncLocations };
};

// ============================================================================
// Main Component
// ============================================================================

const MapPage: React.FC = () => {
  const { locations, isLoading, lastSynced, syncError, syncLocations } = useLocations();
  const { businesses, loading: businessesLoading } = useBusinesses({
    limit: 10000,
    fields: 'id,name,address,location_lat,location_lng,verified,business_type,capacity',
  });

  // Layer toggles
  const [showBusinessPins, setShowBusinessPins] = useState(true);
  const [showStateOverlay, setShowStateOverlay] = useState(true);

  // Business status filter
  const [statusFilter, setStatusFilter] = useState<BusinessStatusFilter>('all');

  const filteredBusinesses = useMemo(
    () => filterBusinesses(businesses, statusFilter),
    [businesses, statusFilter]
  );

  const handleStateClick = (_stateAbbr: string, _stateInfo: StateInfo | undefined): void => {
    // noop — map clicks handled within map component
  };

  const sortedLocations = sortLocationsByStatus(Object.entries(locations));

  return (
    <div className="min-h-screen pb-16 bg-gradient-to-b from-[#0a0a14] to-gray-900">
      <HeroSection
        stateCount={Object.keys(locations).length}
        businessCount={businesses.length}
      />

      <SyncStatusBar
        lastSynced={lastSynced}
        syncError={syncError}
        isLoading={isLoading}
        onSync={syncLocations}
      />

      <section className="max-w-[1200px] mx-auto p-8">
        <MapControlsBar
          showBusinessPins={showBusinessPins}
          onToggleBusinessPins={() => setShowBusinessPins(p => !p)}
          showStateOverlay={showStateOverlay}
          onToggleStateOverlay={() => setShowStateOverlay(p => !p)}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          businessesLoading={businessesLoading}
        />

        <div className="bg-gradient-to-br from-[#1e1e28]/80 to-[#28283a]/80 rounded-[20px] p-8 border border-gray-700/50 shadow-xl">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <USMap
              highlightedStates={locations}
              onStateClick={handleStateClick}
              showTooltip={true}
              businesses={showBusinessPins ? filteredBusinesses : undefined}
              showStateOverlay={showStateOverlay}
            />
          )}
        </div>
        <MapLegend />
      </section>

      <section className="py-12 px-8">
        <h2 className="text-center text-3xl font-bold text-white mb-8">Our Locations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
          {sortedLocations.map(([abbr, loc]) => (
            <LocationCard key={abbr} abbr={abbr} location={loc} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default MapPage;
